import React, { useState } from 'react';
import { generateSourcingStrategies } from '../services/geminiService';
import { supabase_update, supabase } from '../services/supabaseService';
import { Vacancy, SourcingStrategy } from '../types';
import { Globe, Search, Copy, ExternalLink, RefreshCw, Wand2, Lightbulb } from 'lucide-react';

interface VacancySourcingProps {
    vacancy: Vacancy;
    onUpdate: (data: any) => void;
}

const VacancySourcing: React.FC<VacancySourcingProps> = ({ vacancy, onUpdate }) => {
    const [strategies, setStrategies] = useState<SourcingStrategy[]>(vacancy.data.sourcingStrategies || []);
    const [isGenerating, setIsGenerating] = useState(false);

    const handleGenerate = async () => {
        setIsGenerating(true);
        try {
            const result = await generateSourcingStrategies(
                { title: vacancy.title, description: vacancy.description },
                vacancy.data.persona || ''
            );

            const newStrategies = result.strategies;
            setStrategies(newStrategies);
            onUpdate({ sourcingStrategies: newStrategies });

            // Save to DB
            await supabase_update(vacancy.id, {
                data: { ...vacancy.data, sourcingStrategies: newStrategies }
            });

        } catch (e) {
            console.error(e);
            alert("Fout bij genereren sourcing strategieën.");
        } finally {
            setIsGenerating(false);
        }
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        // Could add toast here
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

            <div className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-[2rem] border border-indigo-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>

                <div className="relative z-10">
                    <h2 className="text-2xl font-black text-indigo-900 mb-2 flex items-center gap-3">
                        <Globe className="text-indigo-600" /> AI Web Sourcing
                    </h2>
                    <p className="text-indigo-700/80 max-w-2xl mb-8 font-medium">
                        Laat Froukje het open web afspeuren. We genereren geavanceerde X-Ray en Boolean strings om kandidaten te vinden op LinkedIn, GitHub, en niche platforms.
                    </p>

                    <button
                        onClick={handleGenerate}
                        disabled={isGenerating}
                        className="group relative inline-flex items-center justify-center px-8 py-4 font-black text-white transition-all duration-200 bg-indigo-600 font-lg rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600 disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-indigo-200 hover:shadow-indigo-300 hover:-translate-y-0.5"
                    >
                        {isGenerating ? (
                            <>
                                <RefreshCw className="w-5 h-5 mr-3 animate-spin" />
                                Strategieën Genereren...
                            </>
                        ) : (
                            <>
                                <Wand2 className="w-5 h-5 mr-3 group-hover:rotate-12 transition-transform" />
                                Genereer Sourcing Paden
                            </>
                        )}
                    </button>
                </div>
            </div>

            {strategies.length > 0 && (
                <div className="grid grid-cols-1 gap-6">
                    {strategies.map((strategy, idx) => (
                        <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all group">
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="bg-slate-100 p-3 rounded-xl">
                                        {strategy.platform.toLowerCase().includes('linkedin') ? <Globe size={20} className="text-red-" /> :
                                            strategy.platform.toLowerCase().includes('github') ? <Globe size={20} className="text-slate-800" /> :
                                                <Search size={20} className="text-emerald-600" />}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-slate-900 text-lg">{strategy.platform}</h3>
                                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">X-Ray Search</span>
                                    </div>
                                </div>

                                <a
                                    href={strategy.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-indigo-100 transition-colors"
                                >
                                    <ExternalLink size={14} /> Open in Google
                                </a>
                            </div>

                            {/* TARGET PERSONA BLOCKS */}
                            {strategy.targetPersona && (
                                <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {/* BIO CARD */}
                                    <div className="md:col-span-2 bg-slate-50 p-4 rounded-xl border border-slate-100">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                                            <span className="text-xs font-bold text-slate-500 uppercase">Target Persona</span>
                                        </div>
                                        <p className="text-sm text-slate-700 font-medium leading-relaxed italic">"{strategy.targetPersona.bio}"</p>
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {strategy.targetPersona.currentJobTitles?.map((title, i) => (
                                                <span key={i} className="px-2 py-1 bg-white border border-slate-200 rounded text-[10px] font-bold text-slate-600">{title}</span>
                                            ))}
                                        </div>
                                    </div>

                                    {/* VISUAL STATS */}
                                    <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100 flex flex-col justify-center space-y-3">
                                        <div>
                                            <span className="text-[10px] font-bold text-indigo-400 uppercase block mb-1">Senioriteit</span>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-2 bg-indigo-200 rounded-full overflow-hidden">
                                                    <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${strategy.targetPersona.seniorityLevel || 50}%` }}></div>
                                                </div>
                                                <span className="text-xs font-black text-indigo-900">{strategy.targetPersona.seniorityLabel}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-[10px] font-bold text-indigo-400 uppercase block mb-1">Opleidingsniveau</span>
                                            <span className="text-sm font-black text-indigo-900">{strategy.targetPersona.educationLevel}</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* SKILLS & KEYWORDS */}
                            {strategy.targetPersona && (
                                <div className="mb-4 flex flex-wrap gap-2">
                                    {strategy.targetPersona.skills?.map((skill, i) => (
                                        <span key={i} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-bold border border-emerald-100 flex items-center gap-1">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                            {skill}
                                        </span>
                                    ))}
                                    {strategy.targetPersona.keywords?.map((kw, i) => (
                                        <span key={i} className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-xs font-medium border border-slate-200">
                                            #{kw}
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 font-mono text-xs text-slate-600 break-all leading-relaxed relative group-hover:bg-indigo-50/30 group-hover:border-indigo-100 transition-colors">
                                {strategy.query}
                                <button
                                    onClick={() => copyToClipboard(strategy.query)}
                                    className="absolute top-2 right-2 p-2 bg-white text-slate-400 hover:text-indigo-600 rounded-lg border border-slate-200 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Kopieer query"
                                >
                                    <Copy size={14} />
                                </button>
                            </div>

                            <div className="mt-4 flex items-start gap-2">
                                <Lightbulb size={16} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-sm text-slate-500 font-medium italic">{strategy.explanation}</p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
};

export default VacancySourcing;
